import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileText, Upload, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import API from "@/apiConfig";

export default function FormSubmitPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [responses, setResponses] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    axios.get(`${API}/forms/${slug}`)
      .then((r) => {
        setForm(r.data);
        const initial = {};
        (r.data.fields || []).forEach((f) => {
          initial[f.id] = f.type === "checkbox" ? [] : "";
        });
        setResponses(initial);
      })
      .catch((err) => {
        if (err.response?.status === 401) setNeedsAuth(true);
        else setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const updateField = (fieldId, value) => {
    setResponses((prev) => ({ ...prev, [fieldId]: value }));
    setErrors((prev) => ({ ...prev, [fieldId]: null }));
  };

  const toggleCheckbox = (fieldId, option) => {
    setResponses((prev) => {
      const current = prev[fieldId] || [];
      const updated = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [fieldId]: updated };
    });
    setErrors((prev) => ({ ...prev, [fieldId]: null }));
  };

  const handleFileUpload = async (fieldId, file) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [fieldId]: true }));
    setErrors((prev) => ({ ...prev, [fieldId]: null }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/forms/${slug}/upload`, formData);
      setUploadedFiles((prev) => ({ ...prev, [fieldId]: res.data }));
      setResponses((prev) => ({ ...prev, [fieldId]: res.data.url }));
      toast.success(`${res.data.original_name} uploaded`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Upload failed";
      setErrors((prev) => ({ ...prev, [fieldId]: detail }));
      toast.error(detail);
    } finally {
      setUploading((prev) => ({ ...prev, [fieldId]: false }));
    }
  };

  const validate = () => {
    const newErrors = {};
    (form.fields || []).forEach((f) => {
      if (f.required) {
        const val = responses[f.id];
        if (f.type === "checkbox") {
          if (!val || val.length === 0) newErrors[f.id] = `${f.label} is required`;
        } else if (!val || (typeof val === "string" && !val.trim())) {
          newErrors[f.id] = `${f.label} is required`;
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/forms/${slug}/submit`, {
        responses,
        submitter_name: responses[form.fields?.find((f) => f.type === "text" && f.label.toLowerCase().includes("name"))?.id] || "",
        submitter_email: responses[form.fields?.find((f) => f.type === "email")?.id] || "",
      });
      setSubmitted(true);
      toast.success("Your response has been submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.id];
    const baseClass = error ? "border-red-400" : "border-bone-dark focus:border-gold";

    switch (field.type) {
      case "text":
      case "email":
      case "phone":
      case "number":
        return (
          <Input
            data-testid={`form-field-${field.id}`}
            type={field.type === "phone" ? "tel" : field.type}
            placeholder={field.placeholder || ""}
            value={responses[field.id] || ""}
            onChange={(e) => updateField(field.id, e.target.value)}
            className={`rounded-sm ${baseClass}`}
          />
        );
      case "date":
        return (
          <Input
            data-testid={`form-field-${field.id}`}
            type="date"
            value={responses[field.id] || ""}
            onChange={(e) => updateField(field.id, e.target.value)}
            className={`rounded-sm ${baseClass}`}
          />
        );
      case "textarea":
        return (
          <Textarea
            data-testid={`form-field-${field.id}`}
            placeholder={field.placeholder || ""}
            value={responses[field.id] || ""}
            onChange={(e) => updateField(field.id, e.target.value)}
            className={`rounded-sm min-h-[100px] ${baseClass}`}
          />
        );
      case "select":
        return (
          <Select
            value={responses[field.id] || ""}
            onValueChange={(val) => updateField(field.id, val)}
          >
            <SelectTrigger data-testid={`form-field-${field.id}`} className={`rounded-sm ${baseClass}`}>
              <SelectValue placeholder={field.placeholder || "Select an option"} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "radio":
        return (
          <RadioGroup
            data-testid={`form-field-${field.id}`}
            value={responses[field.id] || ""}
            onValueChange={(val) => updateField(field.id, val)}
            className="space-y-2"
          >
            {(field.options || []).map((opt) => (
              <div key={opt} className="flex items-center gap-2">
                <RadioGroupItem value={opt} id={`${field.id}-${opt}`} />
                <Label htmlFor={`${field.id}-${opt}`} className="text-sm text-gray-700 cursor-pointer">{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        );
      case "checkbox":
        return (
          <div data-testid={`form-field-${field.id}`} className="space-y-2">
            {(field.options || []).map((opt) => (
              <div key={opt} className="flex items-center gap-2">
                <Checkbox
                  id={`${field.id}-${opt}`}
                  checked={(responses[field.id] || []).includes(opt)}
                  onCheckedChange={() => toggleCheckbox(field.id, opt)}
                />
                <Label htmlFor={`${field.id}-${opt}`} className="text-sm text-gray-700 cursor-pointer">{opt}</Label>
              </div>
            ))}
          </div>
        );
      case "file":
        return (
          <div data-testid={`form-upload-${field.id}`}>
            {uploadedFiles[field.id] ? (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="text-sm text-emerald-700">{uploadedFiles[field.id].original_name}</span>
                <button
                  type="button"
                  onClick={() => {
                    setUploadedFiles((prev) => { const n = { ...prev }; delete n[field.id]; return n; });
                    setResponses((prev) => ({ ...prev, [field.id]: "" }));
                  }}
                  className="ml-auto text-xs text-gray-400 hover:text-red-500"
                >
                  Remove
                </button>
              </div>
            ) : uploading[field.id] ? (
              <div className="flex items-center gap-2 p-3 bg-gold/5 border border-gold/20 rounded-sm">
                <Loader2 className="h-4 w-4 animate-spin text-gold" />
                <span className="text-sm text-gray-600">Uploading...</span>
              </div>
            ) : (
              <label className={`flex items-center gap-2 p-3 border rounded-sm cursor-pointer hover:bg-bone/50 transition-colors ${error ? "border-red-400" : "border-bone-dark"}`}>
                <Upload className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-500">Choose file</span>
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => handleFileUpload(field.id, e.target.files?.[0])}
                />
              </label>
            )}
          </div>
        );
      default:
        return <Input value={responses[field.id] || ""} onChange={(e) => updateField(field.id, e.target.value)} className="rounded-sm" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
      </Layout>
    );
  }

  if (needsAuth) {
    return (
      <Layout>
        <div className="container-page py-20 text-center" data-testid="form-needs-auth">
          <FileText className="h-12 w-12 text-gold mx-auto mb-4" />
          <h2 className="font-heading text-2xl text-navy mb-2">Sign In Required</h2>
          <p className="text-gray-500 mb-6">This form requires you to be signed in.</p>
          <Button
            className="bg-gold text-navy hover:bg-gold/90"
            onClick={() => {
              localStorage.setItem("wagc_auth_redirect", `/forms/${slug}`);
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/auth-callback")}`;
            }}
          >
            Sign In with Google
          </Button>
        </div>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout>
        <div className="container-page py-20 text-center" data-testid="form-not-found">
          <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl text-navy mb-2">Form Not Found</h2>
          <p className="text-gray-500 mb-6">This form may have been removed or is no longer available.</p>
          <Button variant="outline" onClick={() => navigate("/forms")}>Back to Forms</Button>
        </div>
      </Layout>
    );
  }

  if (submitted) {
    return (
      <Layout>
        <div className="container-page py-20 text-center" data-testid="form-success">
          <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="font-heading text-2xl text-navy mb-2">Thank You!</h2>
          <p className="text-gray-500 mb-6">Your response has been submitted successfully.</p>
          <Button variant="outline" onClick={() => navigate("/forms")}>Back to Forms</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-navy text-white py-12 sm:py-16 text-center">
        <div className="container-page">
          <div className="w-10 h-[3px] bg-gold mx-auto mb-4" />
          <h1 className="font-heading text-3xl sm:text-4xl text-white mb-2">{form.title}</h1>
          {form.description && <p className="text-bone/60 text-sm max-w-lg mx-auto">{form.description}</p>}
        </div>
      </div>

      <div className="container-page py-10 sm:py-14 max-w-2xl mx-auto">
        <Card className="border-t-2 border-gold">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSubmit} data-testid="form-submit-page" className="space-y-6">
              {(form.fields || []).map((field) => (
                <div key={field.id} className="space-y-1.5">
                  <Label className="text-navy text-sm font-medium">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  {renderField(field)}
                  {errors[field.id] && (
                    <p className="text-red-500 text-xs mt-1">{errors[field.id]}</p>
                  )}
                </div>
              ))}

              <Button
                type="submit"
                data-testid="form-submit-btn"
                disabled={submitting}
                className="w-full bg-gold text-navy hover:bg-gold/90 h-11 font-bold uppercase tracking-wider text-sm rounded-sm"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {submitting ? "Submitting..." : "Submit"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
